import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Spinner,
  Pagination,
} from "@nextui-org/react";
import { IoMdEye } from "react-icons/io";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { refreshIfNeeded } from "../store/actions/refreshIfNeeded";

/* ------------------------------ helpers -------------------------------- */
function getSupplierId() {
  try {
    const details = JSON.parse(localStorage.getItem("user_details") || "{}");
    return details?.user?.id ?? localStorage.getItem("user_id");
  } catch {
    return localStorage.getItem("user_id");
  }
}

function minutesAgo(dateString) {
  const t = new Date(dateString);
  if (Number.isNaN(t.getTime())) return "-";
  const diffSec = Math.floor((Date.now() - t) / 1000);
  if (diffSec < 60) return "Just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min${diffMin > 1 ? "s" : ""} ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr${diffHr > 1 ? "s" : ""} ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay} day${diffDay > 1 ? "s" : ""} ago`;
}

/* --------------------------- component --------------------------------- */
export default function HiddenDataTable() {
  const dispatch = useDispatch();
  const rowsPerPage = 6;
  const batchSize = 30; // batch fetch size
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sortDescriptor, setSortDescriptor] = useState({ column: null, direction: null });

  /* ---------------- refs for batching ---------------- */
  const batchPageRef = useRef(0);
  const fetchingRef = useRef(false);
  const allDataLoadedRef = useRef(false);

  /* -------- ensure fresh token before request -------- */
  const ensureAuth = useCallback(async () => {
    await dispatch(refreshIfNeeded());
    const fresh = localStorage.getItem("accessToken");
    return { headers: { Authorization: `Bearer ${fresh}` } };
  }, [dispatch]);

  /* ---------------- batch fetch ---------------- */
  const fetchBatch = useCallback(
    async (batchNum, { replace = false } = {}) => {
      if (fetchingRef.current) return;
      if (allDataLoadedRef.current && !replace) return;

      const supplierId = getSupplierId();
      if (!supplierId) return;

      try {
        fetchingRef.current = true;
        setLoading(true);

        const auth = await ensureAuth();
        const params = {
          page: batchNum,
          pageSize: batchSize,
        };
        if (sortDescriptor.column) {
          params.sortBy = sortDescriptor.column;
          params.sortOrder = sortDescriptor.direction === "ascending" ? "asc" : "desc";
        }

        const { data: payload } = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/quotes/gethiderequets/${supplierId}`,
          { ...auth, params }
        );

        const dataRows = payload?.data || [];
        const indexObj = payload?.index || {};
        const backendCurrentPage = indexObj.currentPage || batchNum;
        const backendTotalRecords = indexObj.totalRecords || 0;

        setTotalPages(Math.ceil(backendTotalRecords / rowsPerPage));

        setRows((prev) => {
          if (replace || batchNum === 1) return dataRows;
          const prevIds = new Set(prev.map((r) => r.id));
          const uniqueNew = dataRows.filter((r) => !prevIds.has(r.id));
          return [...prev, ...uniqueNew];
        });

        batchPageRef.current = backendCurrentPage;
        allDataLoadedRef.current = dataRows.length < batchSize;
      } catch (err) {
        console.error("fetchBatch error:", err);
        toast.error(err?.response?.data?.message || "Failed to fetch hidden requests");
      } finally {
        fetchingRef.current = false;
        setLoading(false);
      }
    },
    [ensureAuth, sortDescriptor]
  );

  /* ------------- ensure page data loaded ------------- */
  const ensurePageData = useCallback(
    async (uiPage) => {
      const requiredEndIndex = uiPage * rowsPerPage;
      if (requiredEndIndex <= rows.length) return;
      if (allDataLoadedRef.current) return;

      const requiredBatch = Math.ceil(requiredEndIndex / batchSize);
      let startBatch = batchPageRef.current || 0;
      for (let b = startBatch + 1; b <= requiredBatch; b++) {
        await fetchBatch(b);
        if (allDataLoadedRef.current) break;
      }
    },
    [rows.length, rowsPerPage, batchSize, fetchBatch]
  );

  /* ---------------- initial + page data fetch ---------------- */
  useEffect(() => { fetchBatch(1, { replace: true }); }, [fetchBatch]);
  useEffect(() => { ensurePageData(page); }, [page, ensurePageData]);

  /* ---------------- hide / un-hide ---------------- */
  const handleHide = async (requestId) => {
    try {
      const auth = await ensureAuth();
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/quotes/hiderequest/${requestId}`,
        {},
        auth
      );
      toast.success("Request un-hidden", { autoClose: 3000 });
      fetchBatch(1, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to un-hide", { autoClose: 4000 });
    }
  };

  /* ---------------- sorting logic ---------------- */
  const getSortValue = (item, column) => {
    const req = item.request || {};
    const user = item.User || {};
    switch (column) {
      case "status": return req.RequestStatus?.status?.toLowerCase() || "";
      case "car": return `${req.VehicleMake?.name || req.custom_make || ""} ${req.VehicleModel?.name || req.custom_model || ""}`.toLowerCase();
      case "part": return req.Part?.name || req.custom_part_type || "";
      case "created": return new Date(req.date_requested || req.createdAt).getTime() || 0;
      default: return "";
    }
  };

  const sortedItems = useMemo(() => {
    if (!sortDescriptor.column) return rows;
    return [...rows].sort((a, b) => {
      let v1 = getSortValue(a, sortDescriptor.column);
      let v2 = getSortValue(b, sortDescriptor.column);

      if (!isNaN(v1) && !isNaN(v2)) return sortDescriptor.direction === "ascending" ? v1 - v2 : v2 - v1;

      v1 = v1.toString().toLowerCase();
      v2 = v2.toString().toLowerCase();
      return sortDescriptor.direction === "ascending" ? v1.localeCompare(v2) : v2.localeCompare(v1);
    });
  }, [rows, sortDescriptor]);

  const pagedItems = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return sortedItems.slice(start, start + rowsPerPage);
  }, [sortedItems, page, rowsPerPage]);

  const statusColorMap = { New: "primary", "In progress": "warning", Completed: "success", Unknown: "default" };

  /* -------------------------- UI -------------------------- */
  if (loading && rows.length === 0)
    return <div className="flex justify-center items-center h-48"><Spinner size="lg" /></div>;

  return (
    <Table
      aria-label="Hidden Requests Table"
      isStriped
      isHeaderSticky
      sortDescriptor={sortDescriptor}
      onSortChange={setSortDescriptor}
      bottomContent={totalPages > 1 && (
        <div className="py-2 flex justify-center">
          <Pagination page={page} total={totalPages} onChange={setPage} color="primary" showControls />
        </div>
      )}
      bottomContentPlacement="outside"
    >
      <TableHeader columns={[
        { uid: "status", name: "Status", allowsSorting: true },
        { uid: "industry", name: "Industry", allowsSorting: true },
        { uid: "car", name: "Car", allowsSorting: true },
        { uid: "part", name: "Part", allowsSorting: true },
        { uid: "created", name: "Created", allowsSorting: true },
        { uid: "unhide", name: "UnHide", allowsSorting: false }
      ]}>
        {(column) => (
          <TableColumn key={column.uid} allowsSorting={column.allowsSorting}>{column.name}</TableColumn>
        )}
      </TableHeader>

      <TableBody emptyContent={<div className="py-10 text-center text-gray-500 text-sm">No Data Found</div>}>
        {pagedItems.map(({ request, User }) => {
          if (!request) return null;
          const statusLabel = request.RequestStatus?.status || "Unknown";
          const statusColor = statusColorMap[statusLabel] || "default";
          const industryLabel = User?.business_name || User?.Industry || "Private";
          const make = request.VehicleMake?.name || request.custom_make || "-";
          const model = request.VehicleModel?.name || request.custom_model || "-";
          const partLabel = request.custom_part_type || request.Part?.name || "-";
          const createdAgo = minutesAgo(request.date_requested);
          const createdDate = new Date(request.date_requested).toLocaleDateString();

          return (
            <TableRow key={request.id} className="hover:bg-gray-50">
              <TableCell><Chip variant="flat" color={statusColor} className="capitalize">{statusLabel}</Chip></TableCell>
              <TableCell>{industryLabel}</TableCell>
              <TableCell>{`${make} ${model}`}</TableCell>
              <TableCell className="capitalize">{partLabel}</TableCell>
              <TableCell>
                <div className="flex flex-col">
                  <span className="font-semibold">{createdAgo}</span>
                  <span className="text-xs text-gray-500">{createdDate}</span>
                </div>
              </TableCell>
              <TableCell className="text-start pl-8">
                <button onClick={() => handleHide(request.id)}>
                  <IoMdEye className="text-lg text-blue-500 hover:text-blue-700" />
                </button>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
